import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, InputGroup } from 'react-bootstrap';
import { postRequest } from '../../../utils/api';
import { toast } from "react-toastify";

interface ManageCreditsModalProps {
  show: boolean;
  onHide: () => void;
  user: any;
  onSuccess: () => void;
}

const ManageCreditsModal = ({ show, onHide, user, onSuccess }: ManageCreditsModalProps) => {
  const [actionType, setActionType] = useState<'add' | 'deduct'>('add');
  const [amount, setAmount] = useState<number | ''>('');
  const [reason, setReason] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [taxType, setTaxType] = useState('IGST'); // Can be updated dynamically if needed

  // Reset form and set default remark when modal opens or action type changes
  useEffect(() => {
    if (show) {
      setAmount('');
      setActionType('add');
      setReason("Manual Adjustment Fund Add");
    }
  }, [show]);

  // Update remark automatically when toggling between Add and Deduct
  useEffect(() => {
    if (actionType === 'add') {
      setReason("Manual Adjustment Fund Add");
    } else {
      setReason("Manual Adjustment Fund Deduct");
    }
  }, [actionType]);

  // --- DYNAMIC MATH CALCULATIONS ---
  const baseAmount = Number(amount) || 0;
  const taxAmount = baseAmount * 0.18;
  const finalTotal = baseAmount + taxAmount;

  const handleSave = async (ignoreWarning = false) => {
    if (!amount || Number(amount) <= 0) {
      return toast.warning("Please enter a valid amount.");
    }

    setIsSaving(true);
    try {
      const payload = {
        userId: user._id,
        amount: baseAmount,
        tax_amount: taxAmount,
        total_amount: finalTotal,
        type: actionType,
        description: reason,
        ignoreBillingWarning: ignoreWarning
      };

      const response = await postRequest('/user/manage-wallet-credits', payload);

      if (response.status === 'success') {
        toast.success(`Credits ${actionType === 'add' ? 'added' : 'deducted'} successfully!`);
        onSuccess();
        onHide();
      } else if (response.status === 'warning') {
        // Catch the warning and ask the admin
        const userAgreed = window.confirm(response.message);
        if (userAgreed) {
          // Re-run the function, but tell the backend to ignore the warning this time
          handleSave(true);
        }
      } else {
        toast.error(response.message || `Failed to ${actionType} credits.`);
      }
    } catch (error: any) {
      console.error(`Error during credit ${actionType}:`, error);
      toast.error(error.message || "An error occurred while processing credits.");
    } finally {
      setIsSaving(false);
    }
  };

  const isAdd = actionType === 'add';

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Manage Wallet Credits</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {user ? (
          <Form>
            <div className="mb-4">
              <label className="form-label text-muted mb-0">User Details</label>
              <p className="form-control-plaintext fw-bold fs-6">
                {user.user_first_name} {user.user_last_name}
                <br />
                <span className="fs-7 text-muted">{user.user_email}</span>
              </p>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Action</Form.Label>
              <div className="d-flex align-items-center gap-4 mt-1">
                <Form.Check
                  type="radio"
                  id="action-add"
                  name="creditActionType"
                  label="Add Credits"
                  checked={actionType === 'add'}
                  onChange={() => setActionType('add')}
                  disabled={isSaving}
                />
                <Form.Check
                  type="radio"
                  id="action-deduct"
                  name="creditActionType"
                  label="Deduct Credits"
                  checked={actionType === 'deduct'}
                  onChange={() => setActionType('deduct')}
                  disabled={isSaving}
                />
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Amount (Credits)</Form.Label>
              <InputGroup>
                <InputGroup.Text>₹</InputGroup.Text>
                <Form.Control
                  type="number"
                  min="1"
                  placeholder="Enter base amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value === '' ? '' : Number(e.target.value))}
                  disabled={isSaving}
                />
              </InputGroup>
            </Form.Group>

            {/* --- EXACT PROVIDED PAYMENT SECTION --- */}
            {baseAmount > 0 && (
              <div className="card mb-3">
                <div className="card-header py-2">
                  <h6 className="mb-0 fs-14">Calculations</h6>
                </div>
                <div className="card-body py-2">
                  <ul className="payment-summary list-unstyled mb-0">
                    <li className="d-flex justify-content-between mb-1">
                      <span>Base Amount</span>
                      <span>₹{baseAmount.toFixed(2)}</span>
                    </li>

                    {taxType === 'CGST_SGST' ? (
                      <>
                        <li className="d-flex justify-content-between mb-1 text-muted">
                          <span>CGST (9%)</span>
                          <span>₹{(taxAmount / 2).toFixed(2)}</span>
                        </li>
                        <li className="d-flex justify-content-between mb-2 text-muted">
                          <span>SGST (9%)</span>
                          <span>₹{(taxAmount / 2).toFixed(2)}</span>
                        </li>
                      </>
                    ) : (
                      <li className="d-flex justify-content-between mb-2 text-muted">
                        <span>IGST (18%)</span>
                        <span>₹{taxAmount.toFixed(2)}</span>
                      </li>
                    )}

                    <li className="d-flex justify-content-between border-top pt-2">
                      <span className="fw-bold">Total Final Amount</span>
                      <span className="fw-bold">₹{finalTotal.toFixed(2)}</span>
                    </li>
                  </ul>
                </div>
              </div>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Remarks</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Enter remarks..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={isSaving}
              />
            </Form.Group>
          </Form>
        ) : (
          <p>Loading user data...</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant={isAdd ? "success" : "danger"}
          onClick={() => handleSave(false)} // Explicitly pass false to prevent the event object from sneaking in
          disabled={!amount || isSaving}
        >
          {isSaving ? 'Processing...' : (isAdd ? 'Confirm Add' : 'Confirm Deduct')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ManageCreditsModal;